import React, { useRef, useMemo, useEffect } from "react";
import { PublishingPlatforms } from "../constants/sharedTypes";
import { useDomainConfig } from "../hooks/useDomainConfig";
import DomainSelector from "./DomainSelector";

/**
 * BasicInfoSection component - Phần "Thông tin cơ bản" sử dụng chung cho màn hình tạo bài viết
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const BasicInfoSection = ({
  selectedProjectType,
  setSelectedProjectType,
  selectedContentType,
  setSelectedContentType,
  selectedPublishingPlatform,
  setSelectedPublishingPlatform,
  isProjectTypeDropdownOpen,
  setIsProjectTypeDropdownOpen,
  isContentTypeDropdownOpen,
  setIsContentTypeDropdownOpen,
  isPublishingPlatformDropdownOpen,
  setIsPublishingPlatformDropdownOpen,
  projectTypeDropdownRef,
  contentTypeDropdownRef,
  publishingPlatformDropdownRef,
  toggleProjectTypeDropdown,
  toggleContentTypeDropdown,
  togglePublishingPlatformDropdown,
  handleProjectTypeSelect,
  handleContentTypeSelect,
  handlePublishingPlatformSelect,
  isAccordionSection = false,
  isAutoPostGenerator = false,
  onDomainChange,
  domainConfig: propDomainConfig,
  refreshKey: propRefreshKey,
}) => {
  const {
    domainConfig: hookDomainConfig,
    selectedDomain,
    refreshKey,
    isLoading,
  } = useDomainConfig();

  // ✅ FIXED: Ưu tiên domainConfig từ props, fallback về hook
  const domainConfig = propDomainConfig || hookDomainConfig;

  // ✅ FIXED: Sử dụng refreshKey từ props nếu có, fallback về hook
  const currentRefreshKey =
    propRefreshKey !== undefined ? propRefreshKey : refreshKey;

  // ✅ FIXED: Check if domain config is ready
  const isDomainConfigReady =
    domainConfig &&
    !isLoading &&
    domainConfig.projectTypes &&
    domainConfig.contentTypes;

  // ✅ FIXED: Thêm effect để log khi domainConfig thay đổi (debug)
  useEffect(() => {
    console.log("🔍 BasicInfoSection - Domain Config Updated:", {
      selectedDomain,
      domainId: domainConfig?.id,
      domainName: domainConfig?.name,
      propDomainConfig: !!propDomainConfig,
      hookDomainConfig: !!hookDomainConfig,
      usingConfigFrom: propDomainConfig ? "props" : "hook",
      defaultProjectType: domainConfig?.defaults?.projectType,
      defaultContentType: domainConfig?.defaults?.contentType,
      projectTypesCount: Object.keys(domainConfig?.projectTypes || {}).length,
      contentTypesCount: Object.keys(domainConfig?.contentTypes || {}).length,
      currentRefreshKey,
      propRefreshKey,
      hookRefreshKey: refreshKey,
      isLoading,
      isDomainConfigReady,
    });
  }, [
    domainConfig,
    selectedDomain,
    propDomainConfig,
    hookDomainConfig,
    currentRefreshKey,
    propRefreshKey,
    refreshKey,
    isLoading,
    isDomainConfigReady,
  ]);

  // ✅ FIXED: Memoize domain data với dependency đúng
  const { projectTypes, contentTypes, labels, settings } = useMemo(() => {
    return {
      projectTypes: domainConfig?.projectTypes || {},
      contentTypes: domainConfig?.contentTypes || {},
      labels: domainConfig?.labels || {},
      settings: domainConfig?.settings || {},
    };
  }, [domainConfig, selectedDomain, currentRefreshKey]); // ✅ Use currentRefreshKey as dependency

  // Calculate the number of options for display
  const projectTypesCount = Object.keys(projectTypes).length;
  const contentTypesCount = Object.keys(contentTypes).length;

  // Tạo style cho dropdown để tối ưu hóa hiệu suất
  const dropdownStyle = useMemo(
    () => ({
      transition: "all 0.2s ease-in-out",
      transformOrigin: "top center",
      transform: "none",
      willChange: "transform, opacity",
      backfaceVisibility: "hidden",
    }),
    []
  );

  // ✅ FIXED: Sử dụng key ổn định hơn, dựa trên selectedDomain và refreshKey
  const componentKey = `basic-info-${selectedDomain}-${currentRefreshKey}`;

  // ✅ FIXED: Show loading state if domain config is not ready
  if (!isDomainConfigReady) {
    // Get settings for loading state
    const loadingSettings = domainConfig?.settings || {};

    return (
      <div key={componentKey}>
        {/* Domain Selector */}
        <DomainSelector className="mb-6" onDomainChange={onDomainChange} />

        {/* Loading state for dropdowns */}
        {!loadingSettings.hideProjectTypeDropdown && (
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Loại hình
            </label>
            <div className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed animate-pulse">
              <span className="text-gray-500">Đang tải...</span>
            </div>
          </div>
        )}

        {!loadingSettings.hideContentTypeDropdown && (
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Loại nội dung
            </label>
            <div className="w-full p-2 border border-gray-300 rounded bg-gray-100 cursor-not-allowed animate-pulse">
              <span className="text-gray-500">Đang tải...</span>
            </div>
          </div>
        )}

        {/* Publishing Platform Dropdown - Always available */}
        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">
            Tôi muốn tạo nội dung để:
          </label>
          <div className="relative" ref={publishingPlatformDropdownRef}>
            {isAutoPostGenerator ? (
              <div className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-md flex justify-between items-center font-medium cursor-not-allowed">
                <span className="text-gray-700">
                  Đăng bài trên các nền tảng Mạng xã hội (Facebook, Zalo,…)
                </span>
              </div>
            ) : (
              <>
                <div className="dropdown mt-2">
                  <div
                    className="dropdown-button flex items-center justify-between w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
                    onClick={(e) => togglePublishingPlatformDropdown(e)}
                  >
                    <span className="text-gray-700">
                      {selectedPublishingPlatform &&
                      selectedPublishingPlatform.text
                        ? selectedPublishingPlatform.text
                        : "Chọn mục đích tạo nội dung"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                {isPublishingPlatformDropdownOpen && (
                  <div
                    className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg"
                    style={dropdownStyle}
                  >
                    <div className="max-h-60 overflow-y-auto dropdown-content">
                      {Object.entries(PublishingPlatforms).map(
                        ([platform, value]) => (
                          <div
                            key={value}
                            className="dropdown-item p-2 cursor-pointer hover:bg-gray-100"
                            onClick={(e) =>
                              handlePublishingPlatformSelect(platform, e)
                            }
                          >
                            {platform}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div key={componentKey}>
      {/* Domain Selector */}
      <DomainSelector className="mb-6" onDomainChange={onDomainChange} />

      {/* Project Type Dropdown - Now Dynamic - Hide for "other" domain */}
      {!settings.hideProjectTypeDropdown && (
        <div
          className="mb-6"
          key={`project-type-${selectedDomain}-${currentRefreshKey}`}
        >
          <label className="block text-gray-700 font-medium mb-2">
            {labels.projectTypeLabel || "Loại hình"}
          </label>
          <div className="relative" ref={projectTypeDropdownRef}>
            <div className="dropdown mt-2">
              <div
                className="dropdown-button flex items-center justify-between w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
                onClick={(e) => toggleProjectTypeDropdown(e)}
              >
                <span className="text-gray-700">
                  {selectedProjectType || `${projectTypesCount} lựa chọn`}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {isProjectTypeDropdownOpen && (
              <div
                className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg"
                style={dropdownStyle}
                key={`project-type-dropdown-${selectedDomain}-${currentRefreshKey}`}
              >
                <div className="max-h-60 overflow-y-auto dropdown-content">
                  {Object.entries(projectTypes).map(([type, value]) => {
                    console.log(
                      `🔍 Rendering project type option: ${type} for domain ${selectedDomain}`
                    );
                    return (
                      <div
                        key={`${selectedDomain}-${value}`}
                        className="dropdown-item p-2 cursor-pointer hover:bg-gray-100"
                        onClick={(e) => handleProjectTypeSelect(type, e)}
                      >
                        {type}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content Type Dropdown - Now Dynamic - Hide for "other" domain */}
      {!settings.hideContentTypeDropdown && (
        <div
          className="mb-6"
          key={`content-type-${selectedDomain}-${currentRefreshKey}`}
        >
          <label className="block text-gray-700 font-medium mb-2">
            {labels.contentTypeLabel || "Loại nội dung"}
          </label>
          <div className="relative" ref={contentTypeDropdownRef}>
            <div className="dropdown mt-2">
              <div
                className="dropdown-button flex items-center justify-between w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
                onClick={(e) => toggleContentTypeDropdown(e)}
              >
                <span className="text-gray-700">
                  {selectedContentType || `${contentTypesCount} lựa chọn`}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            {isContentTypeDropdownOpen && (
              <div
                className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg"
                style={dropdownStyle}
                key={`content-type-dropdown-${selectedDomain}-${currentRefreshKey}`}
              >
                <div className="max-h-60 overflow-y-auto dropdown-content">
                  {Object.entries(contentTypes).map(([type, value]) => {
                    console.log(
                      `🔍 Rendering content type option: ${type} for domain ${selectedDomain}`
                    );
                    return (
                      <div
                        key={`${selectedDomain}-${value}`}
                        className="dropdown-item p-2 cursor-pointer hover:bg-gray-100"
                        onClick={(e) => handleContentTypeSelect(type, e)}
                      >
                        {type}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Publishing Platform Dropdown */}
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">
          Tôi muốn tạo nội dung để:
        </label>
        <div className="relative" ref={publishingPlatformDropdownRef}>
          {isAutoPostGenerator ? (
            <div className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-md flex justify-between items-center font-medium cursor-not-allowed">
              <span className="text-gray-700">
                Đăng bài trên các nền tảng Mạng xã hội (Facebook, Zalo,…)
              </span>
            </div>
          ) : (
            <>
              <div className="dropdown mt-2">
                <div
                  className="dropdown-button flex items-center justify-between w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
                  onClick={(e) => togglePublishingPlatformDropdown(e)}
                >
                  <span className="text-gray-700">
                    {selectedPublishingPlatform &&
                    selectedPublishingPlatform.text
                      ? selectedPublishingPlatform.text
                      : "Chọn mục đích tạo nội dung"}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {isPublishingPlatformDropdownOpen && (
                <div
                  className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg"
                  style={dropdownStyle}
                >
                  <div className="max-h-60 overflow-y-auto dropdown-content">
                    {Object.entries(PublishingPlatforms).map(
                      ([platform, value]) => (
                        <div
                          key={value}
                          className="dropdown-item p-2 cursor-pointer hover:bg-gray-100"
                          onClick={(e) =>
                            handlePublishingPlatformSelect(platform, e)
                          }
                        >
                          {platform}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;
